import { ACTION_TYPE } from "../core/redux/actionType";
import {
  hasTrippedBreaker,
  updateBreaker,
} from "../core/redux/api/circuitBreaker/module";
import { apiGetEffectiveDateRangeRuleHistory } from "../core/redux/api/endpoints";
import {
  addError,
  addLoading,
  getLoading,
  removeLoadingAndError,
} from "../core/redux/api/module";
import {
  ApiReducer,
  makeApiAction,
  makeApiReducer,
  RsaaTypes,
} from "../core/redux/api/reduxStandardApiAction";
import { IAppState } from "../core/redux/IAppState";
import { assoc, isNilOrEmpty } from "../language/module";
import { getResource } from "../language/resources/getResource";
import * as R from "ramda";
import {
  LIFT_ACCESS_RULE_EVENT_CATEGORY,
  LiftAccessRuleHistory,
} from "./models";
import {
  formatDate,
  convertUtcStringDateToDate,
  formatDateAndTime,
} from "../language/time";

export const KEY_ACTION =
  ACTION_TYPE.GET_EFFECTIVE_DATE_RANGE_RULE_HISTORY_REQUEST;
export const ACTIONS = [
  ACTION_TYPE.GET_EFFECTIVE_DATE_RANGE_RULE_HISTORY_REQUEST,
  ACTION_TYPE.GET_EFFECTIVE_DATE_RANGE_RULE_HISTORY_RESPONSE,
  ACTION_TYPE.GET_EFFECTIVE_DATE_RANGE_RULE_HISTORY_ERROR,
];

/**
 * An action to dispatch to get history for effective date range lift access rules
 */
export const getEffectiveDateRangeHistoryAction = (
  resortAbbreviation: string,
  numberOfResults: number,
  fulfillmentSeasonIds: number[],
) =>
  makeApiAction({
    endpoint: apiGetEffectiveDateRangeRuleHistory(
      resortAbbreviation,
      numberOfResults,
      fulfillmentSeasonIds
        .map((id) => `&fulfillmentSeasonIds=${encodeURIComponent(id)}`)
        .join(""),
    ),
    method: "GET",
    headers: (state: IAppState) => ({
      "content-type": "application/json",
      Authorization: `Bearer ${state.auth.bearerToken}`,
    }),
    types: ACTIONS.map((action) => ({
      type: action,
      meta: { resortAbbreviation },
    })) as RsaaTypes,
    // Bail if already loading results for that query
    bailout: (state: IAppState) =>
      hasTrippedBreaker(state, KEY_ACTION) ||
      (R.path(["selectedResort", "resortAbbreviation"], state) === resortAbbreviation &&
        !!getLoading(state, KEY_ACTION, KEY_ACTION)),
  });

export const reducer: ApiReducer<LiftAccessRuleHistory[]> = (
  state,
  action,
  error,
  isLoading,
  payload,
) => {
  const resort = action.meta.resortAbbreviation;
  const appStateResort = R.path<string>(["selectedResort", "resortAbbreviation"], state);

  // Ignore the results if the response is for a previous query that didn't complete before the
  // user switched resorts
  if (
    action.type ===
      ACTION_TYPE.GET_EFFECTIVE_DATE_RANGE_RULE_HISTORY_RESPONSE &&
    !isNilOrEmpty(appStateResort) &&
    appStateResort !== resort
  ) {
    return state;
  }

  if (isLoading) {
    return addLoading(state, KEY_ACTION, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(
      withBreaker,
      KEY_ACTION,
      KEY_ACTION,
      error ??
        getResource(
          state.locale,
          "GET_EFFECTIVE_DATE_RANGE_RULE_HISTORY_ACTION_ERROR",
        ),
    );
  }

  if (!payload) {
    payload = [];
  }

  const noLoadingOrError = removeLoadingAndError(
    withBreaker,
    KEY_ACTION,
    KEY_ACTION,
  );

  const unknownValue = getResource(state.locale, "LIFT_ACCESS_HISTORY_UNKNOWN");

  // Convert dates to local format
  const dateFormattedPayload = payload.map((history) => {
    history.historyEventDetails = history.historyEventDetails.map((detail) => {
      const modifiedDate = convertUtcStringDateToDate(
        detail,
        "modifiedUtcDateTime",
      );

      detail.modifiedDateFormatted =
        (modifiedDate
          ? formatDateAndTime(
              modifiedDate,
              state.defaultDateFormat,
              state.defaultTimeFormat,
            )
          : undefined) ?? unknownValue; // If the date is invalid or missing, show unknown value

      if (
        detail.eventCategory ===
          LIFT_ACCESS_RULE_EVENT_CATEGORY.EFFECTIVE_DATE_UPDATED ||
        detail.eventCategory ===
          LIFT_ACCESS_RULE_EVENT_CATEGORY.EXPIRATION_DATE_UPDATED
      ) {
        
        detail.valueFormatted =
          (detail.newValue
            ? formatDate(detail.newValue, state.defaultDateFormat)
            : undefined) ?? unknownValue;
      } else {
        detail.valueFormatted = detail.newValue ?? unknownValue;
      }

      return detail;
    });

    return history;
  });

  return assoc(
    "effectiveDateRangeHistory",
    dateFormattedPayload,
    noLoadingOrError,
  );
};

export const getEffectiveDateRangeHistoryReducer = makeApiReducer<
  LiftAccessRuleHistory[]
>(ACTIONS, KEY_ACTION, reducer);
